/**
 * RS Modal — reusable brand modal primitive
 * (PDP v2 Commit 1; no live consumers yet)
 *
 * Mirrors rs-gallery-modal.js conventions.
 *
 * Native <dialog>:
 *   - open  → dialog.showModal()  (native focus trap, ESC dismiss,
 *                                  [open] toggle, backdrop, inert
 *                                  background)
 *   - close → dialog.close()      (native restore-focus to opener)
 *
 * NOTE: there is NO body scroll lock. An earlier version of this
 * comment listed one as a showModal() freebie; it is not. showModal()
 * makes the background inert to interaction, but whether the page
 * behind the dialog still scrolls is browser-dependent, and nothing
 * here sets overflow on <body> (rs-modal.css only sets overflow on
 * .rs-modal and .rs-modal__inner — the dialog and its panel). Adding
 * a lock would be a behaviour change for the swatch modal, so it is
 * deliberately left alone; this note exists so the next reader does
 * not trust the old claim.
 *
 * JS-wired:
 *   - Any element with [data-rs-modal-open="<id>"] opens the dialog
 *     with that id on click.
 *   - .rs-modal__close click → dialog.close()
 *   - Backdrop click → dialog.close() (target === dialog when click
 *     lands outside the inner panel)
 *
 * Public API (idempotent — safe to call before DOM ready):
 *   window.rsModal.open(id)
 *   window.rsModal.close(id)
 *
 * Theme editor compatibility: triggers + close buttons use event
 * delegation on document, so newly-injected sections work without
 * re-init. Re-binds on shopify:section:load for the dialog itself
 * (in case the dialog is inside a freshly-loaded section).
 */

(function () {
  'use strict';

  /* Window-level double-load guard. snippets/rs-modal.liquid emits this
     script tag once PER RENDER CALL — its load-once `unless` cannot dedupe,
     because {% render %} is isolated scope and the assign never escapes (see
     the comment in that file). Two modals on one page therefore ship two
     identical <script src> tags, and identical src does NOT stop a second
     execution: the resource is cached, the script still runs again. Without
     this guard that registers onDocClick on document twice, so every trigger
     click fires the handler twice. Matches rsFormValidateLoaded /
     rsSwatchReturnLoaded elsewhere in this theme. */
  if (window.rsModalLoaded) return;
  window.rsModalLoaded = true;

  var OPEN_ATTR = 'data-rs-modal-open';
  var CLOSE_SELECTOR = '.rs-modal__close';
  var DIALOG_SELECTOR = '.rs-modal';
  var BOUND_FLAG = '__rsModalBound';

  function getDialog(id) {
    if (!id) return null;
    var el = document.getElementById(id);
    if (!el) return null;
    if (!el.classList || !el.classList.contains('rs-modal')) return null;
    return el;
  }

  function openById(id) {
    var dialog = getDialog(id);
    if (!dialog) return false;
    if (typeof dialog.showModal === 'function') {
      try {
        dialog.showModal();
        var panel = dialog.querySelector('.rs-modal__inner');
        if (panel) { panel.setAttribute('tabindex', '-1'); panel.focus(); }
        return true;
      } catch (e) {
        // showModal throws if dialog is already open. Treat as success.
        return dialog.open === true;
      }
    }
    return false;
  }

  function closeById(id) {
    var dialog = getDialog(id);
    if (!dialog) return false;
    if (typeof dialog.close === 'function' && dialog.open) {
      dialog.close();
      return true;
    }
    return false;
  }

  /* ── Document-level delegation ─────────────────────────────
     One delegated click handler covers triggers + close buttons
     for every rs-modal on the page, including ones added after
     init (theme editor section loads, lazy content). */
  function onDocClick(e) {
    var target = e.target;
    if (!target || target.nodeType !== 1) return;

    // Close button — walk up to find .rs-modal__close
    var closeEl = target.closest ? target.closest(CLOSE_SELECTOR) : null;
    if (closeEl) {
      var dialog = closeEl.closest(DIALOG_SELECTOR);
      if (dialog && typeof dialog.close === 'function' && dialog.open) {
        dialog.close();
      }
      return;
    }

    // Trigger button — walk up to find [data-rs-modal-open]
    var trigger = target.closest ? target.closest('[' + OPEN_ATTR + ']') : null;
    if (trigger) {
      var id = trigger.getAttribute(OPEN_ATTR);
      if (id) {
        openById(id);
      }
    }
  }

  /* ── Per-dialog binding ─────────────────────────────────────
     Backdrop click dismissal is a per-dialog listener (target
     === dialog test only works when bound on the dialog itself,
     not on document). Idempotent via BOUND_FLAG. */
  function bindDialog(dialog) {
    if (!dialog || dialog[BOUND_FLAG]) return;
    dialog[BOUND_FLAG] = true;

    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) {
        dialog.close();
      }
    });
  }

  function bindAllDialogs(root) {
    var scope = root || document;
    var dialogs = scope.querySelectorAll
      ? scope.querySelectorAll(DIALOG_SELECTOR)
      : [];
    for (var i = 0; i < dialogs.length; i++) {
      bindDialog(dialogs[i]);
    }
  }

  function init() {
    document.addEventListener('click', onDocClick);
    bindAllDialogs(document);
  }

  /* ── Public API ───────────────────────────────────────────── */
  window.rsModal = {
    open: openById,
    close: closeById
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelectorAll) {
      bindAllDialogs(e.target);
    }
  });
})();
